import React from 'react';
import { Parameter } from '@shared/types';
import { Slider } from '@/components/ui/slider';
import {
  calculateParameterRange,
  calculateParameterStep,
} from '@/utils/parameterUtils';

interface ParameterSliderProps {
  param: Parameter;
  onValueChange: (value: number) => void;
  onValueCommit: (value: number) => void;
  step?: number;
}

function ParameterSliderBase({
  param,
  onValueChange,
  onValueCommit,
  step,
}: ParameterSliderProps) {
  const { min, max } = calculateParameterRange(param);
  const calculatedStep = step ?? calculateParameterStep(param);
  const safeMin =
    typeof param.range?.safeMin === 'number' ? param.range.safeMin : undefined;
  const safeMax =
    typeof param.range?.safeMax === 'number' ? param.range.safeMax : undefined;

  const lastRun = React.useRef(0);
  const timeout = React.useRef<ReturnType<typeof setTimeout> | null>(null);

  const handleThrottledCommit = React.useCallback(
    (value: number) => {
      const now = Date.now();
      if (now - lastRun.current >= 200) {
        onValueCommit(value);
        lastRun.current = now;
      } else {
        if (timeout.current) {
          clearTimeout(timeout.current);
        }
        timeout.current = setTimeout(
          () => {
            onValueCommit(value);
            lastRun.current = Date.now();
            timeout.current = null;
          },
          200 - (now - lastRun.current),
        );
      }
    },
    [onValueCommit],
  );

  React.useEffect(() => {
    return () => {
      if (timeout.current) {
        clearTimeout(timeout.current);
      }
    };
  }, []);

  return (
    <Slider
      id={`${param.name}-slider`}
      name={param.name}
      className="w-full"
      defaultMarkerStyle="line"
      onValueChange={([newValue]) => {
        onValueChange(newValue);
        handleThrottledCommit(newValue);
      }}
      onValueCommit={([newValue]) => {
        if (timeout.current) {
          clearTimeout(timeout.current);
          timeout.current = null;
        }
        onValueCommit(newValue);
        onValueChange(newValue);
        lastRun.current = Date.now();
      }}
      min={min}
      max={max}
      value={[Number(param.value)]}
      defaultValue={[Number(param.defaultValue)]}
      step={calculatedStep}
      safeMin={safeMin}
      safeMax={safeMax}
    />
  );
}

export const ParameterSlider = React.memo(ParameterSliderBase);
